import type { Color, AdvancedFilters } from '@mtg-deck-tool/shared';

export interface SearchFilters {
  type?: string | undefined;
  cmcMin?: number | undefined;
  cmcMax?: number | undefined;
  maxPrice?: number | undefined;
  rarity?: string | undefined;
  keyword?: string | undefined;
}

// ─── Clause-writing mechanism ────────────────────────────────────────────────

/**
 * Update a Scryfall clause in the search bar text.
 *
 * - If `value` is non-empty: finds an existing clause matching `prefix` and replaces it,
 *   or appends if not found.
 * - If `value` is null/empty: removes the clause matching `prefix`.
 *
 * A "clause" is `prefix` followed by a value token. The value token is either:
 * - A quoted string: `o:"draw a card"`
 * - An unquoted token: `t:creature`, `cmc>=3`, `pow>4`
 *
 * @param barText  Current search bar text
 * @param prefix   The operator prefix to match, e.g. `t:`, `o:`, `cmc>=`, `pow>`
 * @param value    The new value (without prefix), or null to remove. Quoted values should include quotes.
 * @returns        Updated bar text
 */
export function updateBarClause(barText: string, prefix: string, value: string | null): string {
  // Escape prefix for regex
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Match the prefix followed by either a quoted string or an unquoted token
  const clausePattern = new RegExp(
    `${escaped}(?:"[^"]*"|\\S+)`,
    'g',
  );

  // Remove all existing clauses with this prefix
  let result = barText.replace(clausePattern, '').replace(/\s{2,}/g, ' ').trim();

  // Append the new clause if value is provided
  if (value !== null && value !== '') {
    const clause = `${prefix}${value}`;
    result = result ? `${result} ${clause}` : clause;
  }

  return result;
}

/**
 * Remove all clauses matching a prefix from bar text.
 */
export function removeBarClause(barText: string, prefix: string): string {
  return updateBarClause(barText, prefix, null);
}

/**
 * Build the final Scryfall query string.
 *
 * The search bar is always a Scryfall syntax input. This function only prepends
 * context filters (commander legality + color identity) and the active tag filter.
 * All other filters (quick + advanced) are already written into the bar text.
 */
export function buildSearchQuery(
  userInput: string,
  _filters: SearchFilters,
  colorIdentity: Color[],
  activeTagSlugs: string[],
  _advancedFilters?: AdvancedFilters,
): string {
  const parts: string[] = [];

  // Context: Commander legality + color identity (always injected, not shown in bar)
  if (colorIdentity.length > 0) {
    parts.push(`commander:${colorIdentity.join('')}`);
  } else {
    parts.push('f:commander');
  }

  // User input (the search bar content — IS the query, including otag: clauses)
  if (userInput.trim()) parts.push(userInput.trim());

  return parts.join(' ');
}
